// @flow
import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';

import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';

import * as DmsCommon from '../../supports/dms-common';
import * as docx from './vendor/docx-preview';
import { fetchFileBuffer, downloadFileBlob } from './fileViewerUtils';

export default function DocxViewer(props) {
  const { fileUrl, filename, canDownload } = props;

  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [fileBlob, setFileBlob] = useState(null);
  const [pageCount, setPageCount] = useState(0);

  const loadDocument = useCallback(async () => {
    if (!fileUrl) return;

    setLoading(true);
    setError(null);

    try {
      const { arrayBuffer, blob } = await fetchFileBuffer(fileUrl);
      setFileBlob(blob);

      if (containerRef.current) {
        containerRef.current.innerHTML = '';

        const options = {
          className: 'docx-rendered-document',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          ignoreFonts: false,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
          experimental: true,
          trimXml: true,
          useBase64URL: true,
        };

        await docx.renderAsync(arrayBuffer, containerRef.current, null, options);

        // Count rendered pages if present
        const pages = containerRef.current.querySelectorAll('.docx-wrapper > section.docx');
        if (pages && pages.length > 0) {
          setPageCount(pages.length);
        }
      }
      setLoading(false);
    } catch (err) {
      console.error('Docx rendering error:', err);
      setError(err);
      setLoading(false);
    }
  }, [fileUrl, filename]);

  useEffect(() => {
    loadDocument();
  }, [loadDocument]);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(2.0, +(prev + 0.1).toFixed(1)));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(0.5, +(prev - 0.1).toFixed(1)));
  };

  const handleZoomReset = () => {
    setZoom(1);
  };

  const handlePrint = () => {
    if (!containerRef.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${filename || 'Document'}</title>
          <style>
            body { margin: 0; padding: 20px; font-family: sans-serif; background: #fff; }
            .docx-wrapper { padding: 0 !important; background: transparent !important; }
            .docx-wrapper > section.docx { box-shadow: none !important; margin: 0 auto 20px auto !important; }
            @media print {
              body { padding: 0; }
              .docx-wrapper > section.docx { page-break-after: always; margin: 0 !important; box-shadow: none !important; }
            }
          </style>
        </head>
        <body>
          ${containerRef.current.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleDownload = () => {
    if (fileBlob) {
      const downloadFilename = filename && filename.toLowerCase().endsWith('.doc')
        ? `${filename.slice(0, -4)}.docx`
        : filename;
      downloadFileBlob(fileBlob, downloadFilename);
    } else if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        bgcolor: '#525659',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Viewer Toolbar */}
      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 0.75,
          bgcolor: '#323639',
          color: '#ffffff',
          borderRadius: 0,
          zIndex: 10,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: '#d1d5db', fontWeight: 500, fontSize: '0.85rem' }}>
            Word Document Viewer
          </Typography>
          {pageCount > 0 && (
            <Typography variant="caption" sx={{ color: '#9ca3af', ml: 1, bgcolor: '#1f2937', px: 1, py: 0.25, borderRadius: 1 }}>
              {pageCount} {pageCount === 1 ? 'page' : 'pages'}
            </Typography>
          )}
        </Box>

        {/* Toolbar Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Zoom Out">
            <span>
              <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 0.5} sx={{ color: '#e5e7eb' }}>
                <ZoomOutIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Typography
            variant="caption"
            sx={{
              minWidth: '45px',
              textAlign: 'center',
              userSelect: 'none',
              color: '#f3f4f6',
              fontFamily: 'monospace',
            }}
          >
            {Math.round(zoom * 100)}%
          </Typography>

          <Tooltip title="Zoom In">
            <span>
              <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 2.0} sx={{ color: '#e5e7eb' }}>
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Reset Zoom (100%)">
            <IconButton size="small" onClick={handleZoomReset} sx={{ color: '#e5e7eb' }}>
              <RestartAltIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5, bgcolor: '#4b5563' }} />

          <Tooltip title="Print Document">
            <IconButton size="small" onClick={handlePrint} sx={{ color: '#e5e7eb' }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {canDownload && (
            <Tooltip title="Download Original File">
              <IconButton size="small" onClick={handleDownload} sx={{ color: '#60a5fa' }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Reload">
            <IconButton size="small" onClick={loadDocument} sx={{ color: '#e5e7eb' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>

      {/* Main Document Body */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: 'auto',
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: loading ? 'center' : 'flex-start',
          p: 2,
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 'auto' }}>
            <CircularProgress size={48} sx={{ color: '#60a5fa', mb: 2 }} />
            <Typography variant="body1" sx={{ color: '#f3f4f6' }}>
              Rendering Word document...
            </Typography>
          </Box>
        )}

        {error && (
          <Box sx={{ maxWidth: 540, mx: 'auto', my: 'auto', p: 2 }}>
            <Alert
              severity="error"
              action={
                canDownload ? (
                  <Button color="inherit" size="small" startIcon={<DownloadIcon />} onClick={handleDownload}>
                    Download
                  </Button>
                ) : null
              }
              sx={{ mb: 2 }}
            >
              {error.message || 'Failed to render Word document'}
            </Alert>
          </Box>
        )}

        {/* DOCX Render Target Container */}
        <Box
          ref={containerRef}
          sx={{
            display: loading || error ? 'none' : 'block',
            transform: `scale(${zoom})`,
            transformOrigin: 'top center',
            transition: 'transform 0.12s ease-out',
            width: '100%',
            '& .docx-wrapper': {
              background: 'transparent !important',
              padding: '16px 0 !important',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            },
            '& .docx-wrapper > section.docx': {
              background: '#ffffff !important',
              boxShadow: '0 6px 18px rgba(0, 0, 0, 0.28) !important',
              marginBottom: '24px !important',
              borderRadius: '2px',
              padding: '60px 72px !important',
            },
          }}
        />
      </Box>
    </Box>
  );
}

DocxViewer.propTypes = {
  fileUrl: PropTypes.string.isRequired,
  filename: PropTypes.string,
  canDownload: PropTypes.bool,
};

DocxViewer.defaultProps = {
  filename: '',
  canDownload: true,
};
